import React, { useCallback, useMemo } from 'react';
import { TouchSearchBar } from '@hqo/react-components-library/dist/molecules/touch-search-bar';
import { Wrapper, StyledCalendarIcon, StyledIconWrapper } from './styles';
import { bookingTimeHandler } from 'utils/bookingTimeHandler';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useTouchSearchBar } from 'hooks/use-touch-search-bar.hook';
import { DateTimeSelectPage } from 'pages/date-time-select-page';
import { Route, Routes } from 'react-router-dom';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { CalendarIcon } from 'floorplan/pages/native-floorplan/components/icons';
import { presetWindowLabelMapper } from 'shared/consts';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';

interface TouchSearchBarComponentProps {
  isResourcesPage?: boolean;
  isFloorplanUi?: boolean;
}

export const TouchSearchBarComponent = ({
  isResourcesPage,
  isFloorplanUi,
}: TouchSearchBarComponentProps): JSX.Element => {
  const { startDate, startDates, startTime, endTime, presetWindows } = useSearchParams();
  const { isMultiDayBookingEnabled } = useAppInstanceConfigFeature();
  const intl = useIntl();
  const locale = useLocale();
  const timeRange = useMemo(
    () => ({ startTime: startTime as string, endTime: endTime as string, locale }),
    [startTime, endTime, locale],
  );
  const startDatesArray: Array<string> = (startDates as string)?.split(',');
  const datesRangeValue = isMultiDayBookingEnabled
    ? bookingTimeHandler(timeRange).getDatesRangeString((startDate as string) ?? startDatesArray)
    : bookingTimeHandler(timeRange).getDateRangeString(startDate as string);

  const {
    reverseHorizontalAnimation,
    reverseAnimation,
    onPressBack,
    onDateClick,
    onNextClick,
    onTimeClick,
    onSearchClick,
    isDateStep,
    onClose,
    backToDateStep,
    onSkipTimeClick,
    isModalWithoutSlideAnimation,
  } = useTouchSearchBar();

  const rightLabel = useMemo(() => {
    return presetWindows
      ? intl.formatMessage({ id: presetWindowLabelMapper.get(String(presetWindows)) })
      : bookingTimeHandler(timeRange).getTimeRangeString();
  }, [presetWindows, intl, timeRange]);

  const icon = isFloorplanUi ? (
    <StyledIconWrapper>
      <CalendarIcon />
    </StyledIconWrapper>
  ) : (
    <StyledCalendarIcon icon={faCalendar} />
  );

  /* The library only wires the click to the label text, so the empty stretch of the field after it
     is dead. A click that lands on the container itself (not on a label) is that stretch. */
  const onEmptyAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('touch-search-bar-container')) {
        onDateClick();
      }
    },
    [onDateClick],
  );

  return (
    <Wrapper
      onClick={onEmptyAreaClick}
      isResourcesPage={isResourcesPage}
      isFloorplanUi={isFloorplanUi}
      className="touch-search-bar-component"
    >
      <Routes>
        <Route
          path="date-time-select"
          element={
            <DateTimeSelectPage
              isModalWithoutSlideAnimation={isModalWithoutSlideAnimation}
              reverseHorizontalAnimation={reverseHorizontalAnimation}
              reverseAnimation={reverseAnimation}
              onClose={onClose}
              onPressBack={onPressBack}
              onNextClick={onNextClick}
              onSkipClick={onSkipTimeClick}
              onSearchClick={onSearchClick}
              isDateStep={isDateStep}
              backToDateStep={backToDateStep}
            />
          }
        />
      </Routes>
      {isResourcesPage ? (
        <TouchSearchBar
          leftLabel={datesRangeValue}
          rightLabel={rightLabel}
          leftPlaceholder={intl.formatMessage({ id: 'date_and_time' })}
          className="touch-search-bar"
          isLeftEnabled
          isRightEnabled
          onLeftLabelClick={onDateClick}
          onRightLabelClick={onTimeClick}
          customIcon={icon}
        />
      ) : (
        <TouchSearchBar
          leftLabel={datesRangeValue}
          rightLabel={bookingTimeHandler(timeRange).getTimeRangeString()}
          leftPlaceholder={intl.formatMessage({ id: 'when_booking' })}
          className="touch-search-bar"
          isLeftEnabled
          isRightEnabled
          onLeftLabelClick={onDateClick}
          onRightLabelClick={onTimeClick}
          customIcon={icon}
        />
      )}
    </Wrapper>
  );
};
